"use client"

import React from "react"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Eye, EyeOff, Upload, AlertCircle, ArrowRight, X, Mail, Phone, Clock } from "lucide-react"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import config from "../config"
import { useNavigate } from "react-router-dom"
import { validationPatterns } from '../utils/validationPatterns';

const LoginInputField = ({ label, name, type, value, onChange, error, icon }) => {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-sm font-medium text-white">
        {label}
      </label>
      <div className="relative">
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className={`w-full px-4 py-3 bg-white/20 backdrop-blur-sm border rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${error ? "border-red-500" : "border-white/30"
            }`}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        {icon && <div className="absolute right-3 top-1/2 transform -translate-y-1/2">{icon}</div>}
      </div>
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-red-300 text-sm"
        >
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </motion.div>
      )}
    </div>
  )
}

export default function TaxLogin() {
  const router = useNavigate()
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpType, setOtpType] = useState("email") // 'email' or 'mobile'
  const [formData, setFormData] = useState({
    pan: "",
    password: "",
    form16: null,
    email: "",
    mobile: "",
  })

  console.log(formData)
  const [otpData, setOtpData] = useState({
    otp: "",
    email: "",
    mobile: "",
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [fileName, setFileName] = useState("")
  const [resendTimer, setResendTimer] = useState(0)
  const [canResend, setCanResend] = useState(false)
  const videoRef = useRef(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5
    }
  }, [])

  // Resend timer effect
  useEffect(() => {
    let interval = null
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((timer) => {
          if (timer <= 1) {
            setCanResend(true)
            return 0
          }
          return timer - 1
        })
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [resendTimer])

  // PAN validation regex
  const panRegex = validationPatterns.PAN
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const mobileRegex = /^[6-9]\d{9}$/

  const validateForm = () => {
    const newErrors = {}

    if (!formData.pan) {
      newErrors.pan = "PAN number is required"
    } else if (!panRegex.test(formData.pan.toUpperCase())) {
      newErrors.pan = "Invalid PAN number format"
    }

    if (!formData.password) {
      newErrors.password = "ITD password is required"
    } else if (formData.password.length < 4) {
      newErrors.password = "Password must be at least 4 characters"
    }

    if (!isLogin) {
      // if (!formData.email) {
      //   newErrors.email = "Email is required"
      // } else if (!emailRegex.test(formData.email)) {
      //   newErrors.email = "Invalid email format"
      // }

      // if (!formData.mobile) {
      //   newErrors.mobile = "Mobile number is required"
      // } else if (!mobileRegex.test(formData.mobile)) {
      //   newErrors.mobile = "Invalid mobile number format"
      // }

      if (!formData.form16) {
        newErrors.form16 = "Form 16 is required"
      }
    }

    return newErrors
  }

  const validateOtp = () => {
    const newErrors = {}
    if (!otpData.otp) {
      newErrors.otp = "OTP is required"
    } else if (otpData.otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits"
    }
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validateForm()
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true)
      const formDataToSend = new FormData()

      if (formData.form16) {
        formDataToSend.append("file", formData.form16)
      }

      // Add email and mobile for registration
      if (!isLogin) {
        formDataToSend.append("email", formData.email)
        formDataToSend.append("mobile", formData.mobile)
      }

      const data = {
        assessee: {
          panNumber: formData.pan.toUpperCase(),
          assessmentYear: formData.password,
          assesId: null
        }
      }



      try {
        const url = isLogin
          ? `${config.apiUrl}/user/profile`
          : `${config.apiUrl}/guest/form16upload/${formData.pan.toUpperCase()}/${formData.password}`

        // const response = await axios.post(url, formDataToSend, {
        //   headers: {
        //     "Content-Type": "multipart/form-data",
        //   },
        // })
        await axios.get(`${config.apiUrl}/user/profile/${formData.pan}/${formData.password}`)
          .then(async (res) => {
            if (isLogin) {
              // Direct login
              toast.success("Logged in successfully!", { position: "top-right" })
              localStorage.setItem("PAN_NO", formData.pan.toUpperCase())
              localStorage.setItem("YEAR", formData.password)
              await new Promise((resolve) => setTimeout(resolve, 2000))
              
              // Check selected return type and redirect accordingly
              const selectedReturnType = localStorage.getItem('SELECTED_RETURN_TYPE')
              if (selectedReturnType === 'tds-return') {
                router("/tds-dashboard/26q")
              } else {
              router("/dashboard")
              }
            } else {
              // Registration - show OTP modal
              setOtpData({
                otp: "",
                email: formData.email,
                mobile: formData.mobile,
              })
              setShowOtpModal(true)
              setResendTimer(120) // 2 minutes
              setCanResend(false)
              toast.success("Registration successful! Please verify your OTP.", { position: "top-right" })
            }
          }).catch(async (err) => {
            const response = await axios.post(url, data)
            if (isLogin) {
              // Direct login
              toast.success("Logged in successfully!", { position: "top-right" })
              localStorage.setItem("PAN_NO", formData.pan.toUpperCase())
              localStorage.setItem("YEAR", formData.password)
              await new Promise((resolve) => setTimeout(resolve, 2000))
              
              // Check selected return type and redirect accordingly
              const selectedReturnType = localStorage.getItem('SELECTED_RETURN_TYPE')
              if (selectedReturnType === 'tds-return') {
                router("/tds-dashboard/26q")
              } else {
              router("/dashboard")
              }
            } else {
              // Registration - show OTP modal
              setOtpData({
                otp: "",
                email: formData.email,
                mobile: formData.mobile,
              })
              setShowOtpModal(true)
              setResendTimer(120) // 2 minutes
              setCanResend(false)
              toast.success("Registration successful! Please verify your OTP.", { position: "top-right" })
            }
          })

        // const response = await axios.post(url, data)

        if (isLogin) {
          // Direct login
          toast.success("Logged in successfully!", { position: "top-right" })
          localStorage.setItem("PAN_NO", formData.pan.toUpperCase())
          localStorage.setItem("YEAR", formData.password)
          await new Promise((resolve) => setTimeout(resolve, 2000))
          router("/return-selection")
        } else {
          // Registration - show OTP modal
          setOtpData({
            otp: "",
            email: formData.email,
            mobile: formData.mobile,
          })
          setShowOtpModal(true)
          setResendTimer(120) // 2 minutes
          setCanResend(false)
          toast.success("Registration successful! Please verify your OTP.", { position: "top-right" })
        }

        // console.log("Response:", response.data)
      } catch (error) {
        console.error("Error:", error)
        toast.error(error.response?.data?.message || "Something went wrong!", { position: "top-right" })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleOtpVerification = async (e) => {
    e.preventDefault()
    const validationErrors = validateOtp()
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0) {
      setIsVerifyingOtp(true)
      try {
        // const response = await axios.post(`${config.apiUrl}/guest/verify-otp`, {
        //   pan: formData.pan,
        //   otp: otpData.otp,
        //   type: otpType,
        //   email: otpData.email,
        //   mobile: otpData.mobile,
        // })

        const response = {
          data: {
            message: "OTP verified successfully!",
          },
        }

        toast.success("OTP verified successfully!", { position: "top-right" })
        localStorage.setItem("PAN_NO", formData.pan.toUpperCase())
        localStorage.setItem("YEAR", formData.password)

        setShowOtpModal(false)
        await new Promise((resolve) => setTimeout(resolve, 1000))
        
        // Check selected return type and redirect accordingly
        const selectedReturnType = localStorage.getItem('SELECTED_RETURN_TYPE')
        if (selectedReturnType === 'tds-return') {
          router("/tds-dashboard")
        } else {
        router("/dashboard")
        }

        console.log("OTP Verification Response:", response.data)
      } catch (error) {
        console.error("OTP Verification Error:", error)
        toast.error(error.response?.data?.message || "Invalid OTP!", { position: "top-right" })
      } finally {
        setIsVerifyingOtp(false)
      }
    }
  }

  const handleResendOtp = async () => {
    if (!canResend) return

    try {
      await axios.post(`${config.apiUrl}/guest/resend-otp`, {
        pan: formData.pan,
        type: otpType,
        email: otpData.email,
        mobile: otpData.mobile,
      })

      toast.success(`OTP sent to your ${otpType}!`, { position: "top-right" })
      setResendTimer(120) // Reset timer to 2 minutes
      setCanResend(false)
    } catch (error) {
      console.error("Resend OTP Error:", error)
      toast.error("Failed to resend OTP!", { position: "top-right" })
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.type === "application/pdf") {
        setFormData({ ...formData, form16: file })
        setFileName(file.name)
        setErrors({ ...errors, form16: "" })
      } else {
        setErrors({ ...errors, form16: "Please upload a PDF file" })
      }
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    // Convert PAN to uppercase automatically
    const processedValue = name === 'pan' ? value.toUpperCase() : value
    setFormData((prevData) => ({
      ...prevData,
      [name]: processedValue,
    }))
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }))
    }
  }

  const handleOtpInputChange = (e) => {
    const { name, value } = e.target
    setOtpData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }))
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <ToastContainer />

      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src="./assets/login_video.mp4?height=1080&width=1920" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div className="absolute top-0 left-0 w-full h-full bg-black/50 z-10"></div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md z-20">
        <div className="backdrop-blur-md bg-black/30 rounded-xl shadow-xl border border-white/20 p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white">{isLogin ? "Welcome Back" : "Create Account"}</h1>
            <p className="text-gray-300 mt-2">
              {isLogin ? "File your ITR easily and quickly" : "Register to start filing your ITR"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <LoginInputField
              label="PAN Number"
              name="pan"
              type="text"
              value={formData.pan}
              onChange={handleInputChange}
              error={errors.pan}
            />

            <LoginInputField
              label="ITD Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              error={errors.password}
              icon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-white/60 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              }
            />

            <AnimatePresence>
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* <LoginInputField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                  />

                  <LoginInputField
                    label="Mobile Number"
                    name="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    error={errors.mobile}
                  /> */}

                  <div className="space-y-2">
                    <label htmlFor="form16" className="block text-sm font-medium text-white">
                      Upload Form 16
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-4 text-center ${errors.form16 ? "border-red-500" : "border-gray-200"
                        } hover:border-cyan-500 transition-colors bg-white/20 backdrop-blur-sm`}
                    >
                      <input type="file" accept=".pdf" onChange={handleFileChange} className="hidden" id="form16" />
                      <label htmlFor="form16" className="cursor-pointer flex flex-col items-center gap-2">
                        <Upload className="w-8 h-8 text-white" />
                        {fileName ? (
                          <span className="text-sm text-white break-all">{fileName}</span>
                        ) : (
                          <span className="text-sm text-gray-300">Click to upload Form 16 (PDF only)</span>
                        )}
                      </label>
                    </div>
                    {errors.form16 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 text-red-300 text-sm"
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.form16}</span>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg px-4 py-2.5 font-medium hover:shadow-lg hover:from-cyan-600 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>{isLogin ? "Login" : "Register"}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin)
                  setErrors({})
                  setFormData({ pan: "", password: "", form16: null, email: "", mobile: "" })
                  setFileName("")
                }}
                className="text-sm text-cyan-300 hover:text-cyan-100 font-medium"
              >
                {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Contact / Support Footer Strip */}
      <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-center py-2.5 px-4 bg-black/40 backdrop-blur-sm border-t border-white/10">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01" />
          </svg>
          <span>Need help?</span>
          <span className="text-white/30">•</span>
          <span>Contact us:</span>
          <a
            href="mailto:admin@taxeases.com"
            className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-200 hover:underline underline-offset-2"
          >
            admin@taxeases.com
          </a>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <AnimatePresence>
        {showOtpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative"
            >
              <button
                onClick={() => setShowOtpModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {otpType === "email" ? (
                    <Mail className="w-8 h-8 text-cyan-600" />
                  ) : (
                    <Phone className="w-8 h-8 text-cyan-600" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify OTP</h2>
                <p className="text-gray-600">
                  We've sent a 6-digit code to your {otpType === "email" ? "email" : "mobile number"}
                </p>
                <p className="text-sm text-cyan-600 font-medium mt-1">
                  {otpType === "email" ? otpData.email : `+91 ${otpData.mobile}`}
                </p>
              </div>

              {/* OTP Type Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                <button
                  type="button"
                  onClick={() => setOtpType("email")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${otpType === "email" ? "bg-white text-cyan-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setOtpType("mobile")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${otpType === "mobile" ? "bg-white text-cyan-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                  <Phone className="w-4 h-4" />
                  Mobile
                </button>
              </div>

              <form onSubmit={handleOtpVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enter 6-digit OTP</label>
                  <input
                    type="text"
                    name="otp"
                    value={otpData.otp}
                    onChange={handleOtpInputChange}
                    maxLength={6}
                    className={`w-full px-4 py-3 border rounded-lg text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.otp ? "border-red-500" : "border-gray-300"
                      }`}
                    placeholder="000000"
                  />
                  {errors.otp && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 text-red-500 text-sm mt-2"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.otp}</span>
                    </motion.div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isVerifyingOtp}
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg px-4 py-3 font-medium hover:shadow-lg hover:from-cyan-600 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isVerifyingOtp ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <span>Verify OTP</span>
                  )}
                </button>

                <div className="text-center">
                  {resendTimer > 0 ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>Resend OTP in {formatTime(resendTimer)}</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
